import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Hourglass } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [error, setError] = useState("");
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    if (!sessionId) { setState("failed"); setError("session_id missing"); return; }
    const poll = async (attempt = 0) => {
      if (stopRef.current) return;
      if (attempt >= MAX_ATTEMPTS) { setState("expired"); return; }
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      } catch (e) {
        setError(formatApiError(e));
        setState("failed");
      }
    };
    poll(0);
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="payment-success-page">
      <div className="border border-white/10 bg-[#0A0A0A] p-10 max-w-lg w-full text-center">
        {state === "polling" && (
          <>
            <Hourglass size={56} weight="duotone" className="text-[#FFB800] mx-auto animate-pulse" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] font-bold mt-4">{t("payment.verifying_tag")}</div>
            <h2 className="font-display text-3xl uppercase font-black mt-2">{t("payment.verifying_title")}</h2>
            <p className="text-zinc-400 mt-3 text-sm">{t("payment.verifying_message")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="text-[#00FF66] mx-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00FF66] font-bold mt-4">{t("payment.paid_tag")}</div>
            <h2 className="font-display text-3xl uppercase font-black mt-2">{t("payment.paid_title")}</h2>
            <p className="text-zinc-400 mt-3 text-sm">{t("payment.paid_message")}</p>
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              <Button onClick={() => navigate("/my-bookings")}
                className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none"
                data-testid="success-view-bookings">
                {t("payment.my_bookings_btn")}
              </Button>
              <Link to="/">
                <Button variant="outline" className="border-white/30 text-white uppercase tracking-[0.2em] font-bold rounded-none">
                  {t("payment.explore_more")}
                </Button>
              </Link>
            </div>
          </>
        )}
        {state === "expired" && (
          <>
            <Hourglass size={56} weight="duotone" className="text-[#FFB800] mx-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] font-bold mt-4">{t("payment.expired_tag")}</div>
            <h2 className="font-display text-3xl uppercase font-black mt-2">{t("payment.expired_title")}</h2>
            <p className="text-zinc-400 mt-3 text-sm">{t("payment.expired_message")}</p>
            <Button onClick={() => navigate("/my-bookings")}
              className="mt-6 bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none"
              data-testid="success-go-bookings">
              {t("payment.my_bookings_btn")}
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={56} weight="fill" className="text-[#E30613] mx-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold mt-4">{t("payment.error_tag")}</div>
            <h2 className="font-display text-3xl uppercase font-black mt-2">{t("payment.error_title")}</h2>
            <p className="text-zinc-400 mt-3 text-sm">{error || t("payment.error_message")}</p>
            <Button onClick={() => navigate("/my-bookings")}
              className="mt-6 bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none">
              {t("payment.my_bookings_btn")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
